import {
  Box,
  Button,
  DialogActionTrigger,
  DialogTitle,
  Input,
  Select,
  Text,
  VStack,
  createListCollection,
} from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useId, useState } from "react";
import { FaFile, FaFolder, FaPlus } from "react-icons/fa";

import { StorageService, ExtractorService } from "@/client";
import type { ApiError } from "@/client/core/ApiError";
import useCustomToast from "@/hooks/useCustomToast";
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTrigger,
} from "../ui/dialog";

interface Folder {
  id: string;
  prefix_name: string;
  description: string;
  user_id: string;
  created_at: string;
  updated_at: string;
}

interface UploadResponse {
  message: string;
  file_info?: {
    filename: string;
    content_type: string;
    size: number;
  };
  files?: Array<{
    filename: string;
    content_type: string;
    size: number;
  }>;
  documents?: Array<{
    id: string;
    file_name: string;
    object_name: string;
    status: string;
  }>;
}

interface FileWithPath extends File {
  webkitRelativePath: string;
}

interface FileWithRelativePath {
  file: File;
  relativePath: string;
}

interface AddFileProps {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  currentFolder?: {
    id: string;
    prefix_name: string;
    folder_name: string;
  };
}

const AddFile = ({ isOpen, setIsOpen, currentFolder }: AddFileProps) => {
  const folderInputId = useId();
  const fileInputId = useId();
  const [selectedFolder, setSelectedFolder] = useState<string>("");
  const [selectedFolderId, setSelectedFolderId] = useState<string>("");
  const [selectedFiles, setSelectedFiles] = useState<FileList | null>(null);
  const [uploadFolder, setUploadFolder] = useState<string>("");
  const [folderFiles, setFolderFiles] = useState<FileWithRelativePath[]>([]);
  const queryClient = useQueryClient();
  const { showSuccessToast, showErrorToast } = useCustomToast();

  // 폴더 목록 조회
  const { data: foldersData } = useQuery({
    queryKey: ["foldersList"],
    queryFn: async () => {
      try {
        const response = await StorageService.listFolders();
        return {
          data: response as Folder[],
          count: (response as Folder[]).length,
        };
      } catch (error) {
        console.error("폴더 목록 조회 실패:", error);
        if (error instanceof Error) {
          console.error("에러 상세:", error.message);
        }
        return { data: [], count: 0 };
      }
    },
  });

  const folders: Folder[] = foldersData?.data ?? [];

  // 다이얼로그가 닫힐 때 selectedFolder 초기화
  useEffect(() => {
    if (!isOpen) {
      setSelectedFolder("");
      setSelectedFolderId("");
    }
  }, [isOpen]);

  // 다이얼로그가 열릴 때마다 currentFolder를 selectedFolder에 반영
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    if (!folders || folders.length === 0) {
      return;
    }

    if (currentFolder) {
      // currentFolder가 prefix_name인 경우
      const foundByPrefix = folders.find(
        (folder: Folder) => folder.prefix_name === currentFolder.prefix_name
      );
      if (foundByPrefix) {
        setSelectedFolder(foundByPrefix.prefix_name);
        setSelectedFolderId(foundByPrefix.id);
        return;
      }

      // currentFolder가 id인 경우
      const foundById = folders.find(
        (folder: Folder) => folder.id === currentFolder.id
      );
      if (foundById) {
        setSelectedFolder(foundById.prefix_name);
        setSelectedFolderId(foundById.id);
        return;
      }
    }

    // 매칭되는 폴더가 없거나 currentFolder가 없는 경우
    setSelectedFolder(folders[0].prefix_name);
    setSelectedFolderId(folders[0].id);
  }, [isOpen, currentFolder, folders]);

  // 다이얼로그가 열릴 때 폼 초기화
  useEffect(() => {
    if (isOpen) {
      // 파일 관련 상태만 초기화
      setSelectedFiles(null);
      setUploadFolder("");
      setFolderFiles([]);

      // input 요소들 초기화
      const fileInput = document.getElementById(
        fileInputId
      ) as HTMLInputElement;
      const folderInput = document.getElementById(
        folderInputId
      ) as HTMLInputElement;
      if (fileInput) fileInput.value = "";
      if (folderInput) folderInput.value = "";
    }
  }, [isOpen, fileInputId, folderInputId]);

  // 폴더 컬렉션 생성
  const folderCollection = createListCollection({
    items: Array.isArray(folders)
      ? folders.map((folder) => ({
          label: folder.prefix_name,
          value: folder.prefix_name,
        }))
      : [],
  });

  // 폴더 선택 핸들러 수정
  const handleFolderSelect = (folderKey: string) => {
    setSelectedFolder(folderKey);
    const selectedFolderObj = folders.find(
      (folder) => folder.prefix_name === folderKey
    );
    if (selectedFolderObj) {
      setSelectedFolderId(selectedFolderObj.id);
    }
  };

  const handleFolderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    // 첫 번째 파일에서 폴더명 가져오기
    const file = files[0] as FileWithPath;
    if (file.webkitRelativePath) {
      const relativePath = file.webkitRelativePath;
      const folderName = relativePath.split("/")[0];

      // 선택된 모든 파일을 상대 경로와 함께 저장
      const filesWithPaths = Array.from(files).map((file) => ({
        file,
        relativePath: (file as FileWithPath).webkitRelativePath,
      }));

      setFolderFiles(filesWithPaths);
      setUploadFolder(folderName);
      setSelectedFiles(null); // 파일 선택 초기화
    } else {
      console.error("폴더 경로를 가져올 수 없습니다.");
      showErrorToast(
        "폴더 경로를 가져올 수 없습니다. 브라우저가 폴더 선택을 지원하지 않을 수 있습니다."
      );
    }
  };

  // 파일 업로드 뮤테이션
  const uploadMutation = useMutation<UploadResponse, ApiError>({
    mutationFn: async () => {
      if (!selectedFolderId) {
        throw new Error("폴더를 선택해주세요.");
      }

      // 인증 토큰 가져오기
      const token = localStorage.getItem("access_token");
      if (!token) {
        throw new Error("로그인이 필요합니다.");
      }

      try {
        if (selectedFiles?.length === 1) {
          const response = await StorageService.uploadFiles({
            formData: {
              context_id: selectedFolderId,
              files: [selectedFiles[0]],
              use_schema: "false",
            },
          });
          const data = response as UploadResponse;
          if (!data.message) throw new Error("파일 업로드에 실패했습니다.");
          return data;
        }

        if (selectedFiles && selectedFiles.length > 1) {
          const response = await StorageService.uploadFiles({
            formData: {
              context_id: selectedFolderId,
              files: Array.from(selectedFiles),
              use_schema: "false",
            },
          });
          const data = response as UploadResponse;
          if (!data.message) throw new Error("파일 업로드에 실패했습니다.");
          return data;
        }

        if (folderFiles.length > 0) {
          const response = await StorageService.uploadFiles({
            formData: {
              context_id: selectedFolderId,
              files: folderFiles.map(({ file }) => file),
              use_schema: "false",
            },
          });
          const data = response as UploadResponse;
          if (!data.message)
            throw new Error("폴더 내 파일 업로드에 실패했습니다.");
          return data;
        }

        throw new Error("파일 또는 폴더를 선택해주세요.");
      } catch (error) {
        if (error instanceof Error) {
          throw error;
        }
        throw new Error("알 수 없는 오류가 발생했습니다.");
      }
    },
    onSuccess: async (data) => {
      // 업로드된 문서들에 대해 processDocument 호출
      if (data.documents && data.documents.length > 0) {
        try {
          for (const document of data.documents) {
            await ExtractorService.processDocument({
              documentId: document.id,
            });
            console.log(
              `문서 처리 시작: ${document.file_name} (ID: ${document.id})`
            );
          }
          showSuccessToast(
            "파일이 성공적으로 업로드되고 문서 처리가 시작되었습니다."
          );
        } catch (error) {
          console.error("문서 처리 시작 실패:", error);
          showSuccessToast(
            data.message || "파일이 성공적으로 업로드되었습니다."
          );
          showErrorToast("문서 처리 시작에 실패했습니다.");
        }
      } else {
        showSuccessToast(data.message || "파일이 성공적으로 업로드되었습니다.");
      }

      resetForm();
      setIsOpen(false);

      // 폴더 목록 갱신
      queryClient.invalidateQueries({
        queryKey: ["foldersList"],
      });

      // 현재 선택된 폴더의 파일 목록 갱신
      if (currentFolder?.id) {
        queryClient.invalidateQueries({
          queryKey: ["filesWithStatus", currentFolder.id],
        });
      }
    },
    onError: (error) => {
      const errorMessage =
        error instanceof Error
          ? error.message
          : typeof error === "object" && error !== null
            ? JSON.stringify(error)
            : "파일 업로드 중 오류가 발생했습니다.";

      showErrorToast(errorMessage);
      console.error("파일 업로드 실패:", error);
    },
    onSettled: () => {
      // 폴더 목록 갱신
      queryClient.invalidateQueries({
        queryKey: ["foldersList"],
      });

      // 현재 선택된 폴더의 파일 목록 갱신
      if (currentFolder?.id) {
        queryClient.invalidateQueries({
          queryKey: ["filesWithStatus", currentFolder.id],
        });
      }
    },
  });

  const resetForm = () => {
    // 폴더 선택은 초기화하지 않고 유지
    setSelectedFiles(null);
    setUploadFolder("");
    setFolderFiles([]);

    // input 요소들 초기화
    const fileInput = document.getElementById(fileInputId) as HTMLInputElement;
    const folderInput = document.getElementById(
      folderInputId
    ) as HTMLInputElement;
    if (fileInput) fileInput.value = "";
    if (folderInput) folderInput.value = "";
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setSelectedFiles(e.target.files);
      setUploadFolder(""); // 파일 선택 시 폴더 선택 초기화
      setFolderFiles([]); // 폴더 파일 목록 초기화
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!selectedFolderId) {
      showErrorToast("폴더를 선택해주세요");
      return;
    }

    if (!selectedFiles && !uploadFolder) {
      showErrorToast("파일 또는 폴더를 선택해주세요");
      return;
    }

    try {
      await uploadMutation.mutateAsync();
    } catch (error) {
      // 에러는 mutation의 onError에서 처리됨
      console.error("업로드 실패:", error);
    }
  };

  return (
    <DialogRoot
      size={{ base: "xs", md: "md" }}
      placement="center"
      open={isOpen}
      onOpenChange={({ open }) => {
        setIsOpen(open);
      }}
    >
      <DialogTrigger asChild>
        <Button value="add-file" my={4} rounded="full">
          <FaPlus fontSize="16px" />
          파일 등록
        </Button>
      </DialogTrigger>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>파일 업로드</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <Text mb={4}>업로드할 파일 또는 폴더를 선택하세요.</Text>
            <VStack gap={4} width="100%">
              <Box width="100%">
                <Text mb={2}>저장할 폴더</Text>
                <Select.Root
                  collection={folderCollection}
                  value={selectedFolder ? [selectedFolder] : []}
                  onValueChange={(details) => {
                    if (details.value?.[0]) {
                      handleFolderSelect(details.value[0]);
                    }
                  }}
                >
                  <Select.HiddenSelect />
                  <Select.Control width="100%">
                    <Select.Trigger width="100%">
                      <Select.ValueText placeholder="폴더를 선택하세요" />
                    </Select.Trigger>
                    <Select.IndicatorGroup>
                      <Select.Indicator />
                    </Select.IndicatorGroup>
                  </Select.Control>
                  <Select.Positioner>
                    <Select.Content width="100%">
                      {folderCollection.items.map((item) => (
                        <Select.Item key={item.value} item={item}>
                          {item.label}
                          <Select.ItemIndicator />
                        </Select.Item>
                      ))}
                    </Select.Content>
                  </Select.Positioner>
                </Select.Root>
                {!selectedFolder && (
                  <Text color="red.500" fontSize="sm" mt={1}>
                    폴더를 선택해주세요
                  </Text>
                )}
              </Box>

              <Box width="100%">
                <Text mb={2}>폴더 선택</Text>
                <Box position="relative" width="100%">
                  <input
                    type="file"
                    onChange={handleFolderChange}
                    style={{ display: "none" }}
                    id={folderInputId}
                    data-webkitdirectory=""
                    data-directory=""
                  />
                  <Box position="relative" width="100%">
                    <Input
                      placeholder="폴더를 선택하세요"
                      value={
                        uploadFolder
                          ? `${uploadFolder} (${folderFiles.length}개 파일)`
                          : ""
                      }
                      readOnly
                      onClick={() => {
                        const input = document.getElementById(
                          folderInputId
                        ) as HTMLInputElement;
                        if (input) {
                          input.setAttribute("webkitdirectory", "");
                          input.setAttribute("directory", "");
                          input.click();
                        }
                      }}
                      width="100%"
                      pr="40px"
                    />
                    <Button
                      aria-label="폴더 선택"
                      onClick={() => {
                        const input = document.getElementById(
                          folderInputId
                        ) as HTMLInputElement;
                        if (input) {
                          input.setAttribute("webkitdirectory", "");
                          input.setAttribute("directory", "");
                          input.click();
                        }
                      }}
                      variant="ghost"
                      position="absolute"
                      right="0"
                      top="0"
                      height="100%"
                      width="40px"
                      minWidth="40px"
                      padding="0"
                    >
                      <FaFolder />
                    </Button>
                  </Box>
                </Box>
              </Box>

              <Box width="100%">
                <Text mb={2}>파일 선택</Text>
                <Box position="relative" width="100%">
                  <input
                    type="file"
                    multiple
                    onChange={handleFileChange}
                    style={{ display: "none" }}
                    id={fileInputId}
                  />
                  <Box position="relative" width="100%">
                    <Input
                      placeholder="파일을 선택하세요"
                      value={
                        selectedFiles
                          ? `${selectedFiles.length}개 파일 선택됨`
                          : ""
                      }
                      readOnly
                      onClick={() =>
                        document.getElementById(fileInputId)?.click()
                      }
                      width="100%"
                      pr="40px"
                    />
                    <Button
                      aria-label="파일 선택"
                      onClick={() =>
                        document.getElementById(fileInputId)?.click()
                      }
                      variant="ghost"
                      position="absolute"
                      right="0"
                      top="0"
                      height="100%"
                      width="40px"
                      minWidth="40px"
                      padding="0"
                    >
                      <FaFile />
                    </Button>
                  </Box>
                </Box>
              </Box>
            </VStack>
          </DialogBody>

          <DialogFooter gap={2}>
            <DialogActionTrigger asChild>
              <Button
                variant="subtle"
                colorPalette="gray"
                disabled={uploadMutation.isPending}
              >
                취소
              </Button>
            </DialogActionTrigger>
            <Button
              variant="solid"
              type="submit"
              disabled={!selectedFolderId || (!selectedFiles && !uploadFolder)}
              loading={uploadMutation.isPending}
            >
              등록
            </Button>
          </DialogFooter>
        </form>
        <DialogCloseTrigger />
      </DialogContent>
    </DialogRoot>
  );
};

export default AddFile;
